import React from 'react';
import {
  ActivityIndicator,
  Image,
  RefreshControl,
  SafeAreaView,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { CalendarWidget } from '../components/CalendarWidget';
import { EventItem } from '../components/EventItem';
import { EventsHeader } from '../components/EventsHeader';
import { OwnershipScopeToggle } from '../components/OwnershipScopeToggle';
import { PeriodSelector } from '../components/PeriodSelector';
import { ScreenTransition } from '../components/ScreenTransition';
import { WeekSelector } from '../components/WeekSelector';
import { useEventsList } from '../hooks/useEventsList';
import styles from './EventsScreen.styles';

interface EventsScreenProps {
  navigation: any;
  onNavigateToFilteredProducts?: (filters: {
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    eventBucket?: 'nextWeek' | 'nextMaturity';
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    maxDistancePct?: number;
    ownershipScope?: 'group' | 'self';
  }) => void;
  onProductPress?: (isin: string) => void;
  ownershipScope?: 'group' | 'self';
  onOwnershipScopeChange?: (value: 'group' | 'self') => void;
  showOwnershipScopeToggle?: boolean;
}

export const EventsScreen: React.FC<EventsScreenProps> = ({ 
  navigation, 
  onNavigateToFilteredProducts,
  onProductPress,
  ownershipScope = 'group',
  onOwnershipScopeChange,
  showOwnershipScopeToggle = true,
}) => {
  const {
    selectedWeekStart,
    setSelectedWeekStart,
    selectedPeriod,
    setSelectedPeriod,
    dashboard,
    loading,
    loadingSummary,
    loadingEvents,
    refreshing,
    error,
    filteredEvents,
    onRefresh,
  } = useEventsList(ownershipScope);

  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;
  const isTablet = width >= 768 && width < 1024;

  const handleWeekChange = (weekStart: string) => {
    setSelectedWeekStart(weekStart);
  };

  const handleClearFilters = () => {
    setSelectedWeekStart(getCurrentWeekStart());
  };

  const navigateToProductsByEvent = (eventType: string, eventBucket: 'nextWeek' | 'nextMaturity') => {
    if (!onNavigateToFilteredProducts) return;
    onNavigateToFilteredProducts({
      eventType,
      eventBucket,
      weekStart: getCurrentWeekStart(),
      ownershipScope,
    });
  };

  const navigateToRiskProducts = (riskType: 'barrier' | 'barrierHit' | 'lowStrike') => {
    if (!onNavigateToFilteredProducts) return;
    onNavigateToFilteredProducts({ nearBarrier: true, riskType, ownershipScope });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getWeekRangeString = () => {
    const weekStart = new Date(selectedWeekStart + 'T12:00:00');
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startDay = weekStart.getDate().toString().padStart(2, '0');
    const endDay = weekEnd.getDate().toString().padStart(2, '0');
    const monthName = weekStart.toLocaleDateString('en-US', { month: 'short' });
    const weekNumber = getWeekNumber(weekStart);
    return `${startDay} - ${endDay} ${monthName} (Week ${weekNumber})`;
  };

  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  /** Monday of current week in YYYY-MM-DD */
  const getCurrentWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);
    return monday.toISOString().slice(0, 10);
  };
  const isDefaultWeek = selectedWeekStart === getCurrentWeekStart();
  const nextWeek = dashboard?.nextWeek;
  const nextMaturity = dashboard?.nextMaturity;

  const nextWeekExpiry = nextWeek?.expiryCount ?? 0;
  const nextWeekCoupon = nextWeek?.couponCount ?? 0;
  const nextWeekAutocall = nextWeek?.autocallCount ?? 0;
  const nextWeekCallable = nextWeek?.callableCount ?? 0;

  const nextMaturityExpiry = nextMaturity?.expiryCount ?? 0;
  const nextMaturityCoupon = nextMaturity?.couponCount ?? 0;
  const nextMaturityAutocall = nextMaturity?.autocallCount ?? 0;
  const nextMaturityCallable = nextMaturity?.callableCount ?? 0;

  const categoryCards = [
    {
      key: 'all',
      label: 'All Events',
      eventType: 'All',
      nextWeek: nextWeekExpiry  + nextWeekAutocall + nextWeekCallable,
      nextMaturity:
        nextMaturityExpiry +
        nextMaturityAutocall +
        nextMaturityCallable,
    },
    {
      key: 'expiry',
      label: 'Expiry',
      eventType: 'Expiry',
      nextWeek: nextWeekExpiry,
      nextMaturity: nextMaturityExpiry,
    },
    {
      key: 'autocall',
      label: 'Autocall',
      eventType: 'Autocall',
      nextWeek: nextWeekAutocall,
      nextMaturity: nextMaturityAutocall,
    },
    {
      key: 'callable',
      label: 'Callable',
      eventType: 'Callable',
      nextWeek: nextWeekCallable,
      nextMaturity: nextMaturityCallable,
    },
    {
      key: 'coupon',
      label: 'Coupon',
      eventType: 'Coupon',
      nextWeek: nextWeekCoupon,
      nextMaturity: nextMaturityCoupon,
    },
  ] as const;

  const ownershipToggle = showOwnershipScopeToggle ? (
    <OwnershipScopeToggle
      ownershipScope={ownershipScope}
      onOwnershipScopeChange={onOwnershipScopeChange}
      thumbColorOn="#2563EB"
    />
  ) : null;

  if (error) {
    return (
      <SafeAreaView style={styles.container}>
        <EventsHeader 
          onBack={() => navigation.goBack()} 
          showSearch={false}
          rightContent={ownershipToggle}
        />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={onRefresh}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <ScreenTransition transitionType="slideFromRight">
      <SafeAreaView style={styles.container}>
        <EventsHeader 
          onBack={() => navigation.goBack()} 
          showSearch={false}
          dateRange={isDesktop ? getWeekRangeString() : undefined}
          rightContent={ownershipToggle}
        />

        <View style={{ flex: 1 }}>
          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor="#0891b2"
                colors={['#0891b2']}
              />
            }
          >
          {/* Week Selector + Clear filters - Solo en mobile/tablet */}
          {!isDesktop && (
            <View style={styles.weekSelectorSection}>
              <WeekSelector
                selectedWeekStart={selectedWeekStart}
                onWeekChange={handleWeekChange}
              />
              {!isDefaultWeek && (
                <TouchableOpacity
                  style={styles.clearFiltersButton}
                  onPress={handleClearFilters}
                  activeOpacity={0.7}
                >
                  <Text style={styles.clearFiltersButtonText}>Clear filters</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Dashboard Cards with Calendar */}
          {(dashboard || loadingSummary) ? (
            <View style={styles.dashboardContainer}>
              <View style={styles.dashboardWithCalendar}>
                <View style={styles.categoriesTable}>
                  <View style={styles.categoriesHeaderRow}>
                    {categoryCards.map((category) => (
                      <View key={category.key} style={styles.categoriesHeaderCell}>
                        <Text style={styles.categoriesHeaderText}>
                          {category.label}
                        </Text>
                      </View>
                    ))}
                  </View>

                  <View style={styles.categoriesDataRow}>
                    {categoryCards.map((category) => (
                      <TouchableOpacity
                        key={`${category.key}-nextWeek`}
                        style={styles.categoriesCardCell}
                        onPress={() => navigateToProductsByEvent(category.eventType, 'nextWeek')}
                        activeOpacity={0.7}
                      >
                        <View
                          style={[
                            styles.dashboardCard,
                            styles.categoriesCard,
                            category.key === 'coupon'
                              ? (isDesktop || isTablet)
                                ? [
                                    styles.couponBoxHighlightDesktop,
                                    styles.dashboardCardDesktop,
                                  ]
                                : styles.couponBoxHighlight
                              : (isDesktop || isTablet)
                                ? [styles.expiryCardDesktop, styles.dashboardCardDesktop]
                                : styles.expiryCard,
                          ]}
                        >
                          <View
                            style={[
                              styles.cardIcon,
                              styles.categoriesCardIconPosition,
                              (isDesktop || isTablet) && styles.cardIconDesktop,
                            ]}
                          >
                            <Image
                              source={require('../../assets/icons/expiry-icon.png')}
                              style={[
                                styles.cardIconImage,
                                (isDesktop || isTablet) && styles.cardIconImageDesktop,
                              ]}
                            />
                          </View>
                          <View style={styles.categoriesCardBody}>
                            <View
                              style={[
                                styles.cardNumberContainer,
                                styles.categoriesCardNumberContainer,
                                (isDesktop || isTablet) && styles.cardNumberContainerDesktop,
                              ]}
                            >
                              {loadingSummary ? (
                                <ActivityIndicator size="small" color="#0891b2" />
                              ) : (
                                <Text style={[styles.cardNumber, styles.categoriesCardNumber]}>
                                  {category.nextWeek}
                                </Text>
                              )}
                            </View>
                            <Text style={styles.categoriesCardSubtitle}>Next Week</Text>
                          </View>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>

                  <View style={styles.categoriesDataRow}>
                    {categoryCards.map((category) => (
                      <TouchableOpacity
                        key={`${category.key}-nextMaturity`}
                        style={styles.categoriesCardCell}
                        onPress={() =>
                          navigateToProductsByEvent(category.eventType, 'nextMaturity')
                        }
                        activeOpacity={0.7}
                      >
                        <View
                          style={[
                            styles.dashboardCard,
                            styles.categoriesCard,
                            category.key === 'coupon'
                              ? (isDesktop || isTablet)
                                ? [
                                    styles.couponBoxHighlightDesktop,
                                    styles.dashboardCardDesktop,
                                  ]
                                : styles.couponBoxHighlight
                              : (isDesktop || isTablet)
                                ? [styles.expiryCardDesktop, styles.dashboardCardDesktop]
                                : styles.expiryCard,
                          ]}
                        >
                          <View
                            style={[
                              styles.cardIcon,
                              styles.categoriesCardIconPosition,
                              (isDesktop || isTablet) && styles.cardIconDesktop,
                            ]}
                          >
                            <Image
                              source={require('../../assets/icons/coupon-icon.png')}
                              style={[
                                styles.cardIconImage,
                                (isDesktop || isTablet) && styles.cardIconImageDesktop,
                              ]}
                            />
                          </View>
                          <View style={styles.categoriesCardBody}>
                            <View
                              style={[
                                styles.cardNumberContainer,
                                styles.categoriesCardNumberContainer,
                                (isDesktop || isTablet) && styles.cardNumberContainerDesktop,
                              ]}
                            >
                              {loadingSummary ? (
                                <ActivityIndicator size="small" color="#0891b2" />
                              ) : (
                                <Text style={[styles.cardNumber, styles.categoriesCardNumber]}>
                                  {category.nextMaturity}
                                </Text>
                              )}
                            </View>
                            <Text style={styles.categoriesCardSubtitle}>Next Month</Text>
                          </View>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View
                  style={[
                    styles.dashboardBottomRow,
                    !isDesktop && styles.dashboardBottomRowMobile,
                  ]}
                >
                  <View style={styles.productsAtRiskSection}>
                    {(
                      [
                        {
                          key: 'lowStrike',
                          title: 'Strike monitor',
                          riskType: 'lowStrike' as const,
                          value: dashboard?.productsLowStrike ?? 0,
                          variant: 'neutral' as const,
                        },
                        {
                          key: 'barrier',
                          title: 'Barrier monitor',
                          riskType: 'barrier' as const,
                          value: dashboard?.productsAtRiskBelow ?? 0,
                          variant: 'neutral' as const,
                        },
                        {
                          key: 'barrierHit',
                          title: 'Barrier hit',
                          riskType: 'barrierHit' as const,
                          value: dashboard?.productsBarrierHit ?? 0,
                          variant: 'danger' as const,
                        },
                      ] as const
                    ).map((item) => (
                      <TouchableOpacity
                        key={item.key}
                        style={styles.productsAtRiskMiniCardShell}
                        onPress={() => navigateToRiskProducts(item.riskType)}
                        activeOpacity={0.7}
                      >
                        <View
                          style={[
                            styles.productsAtRiskMiniCard,
                            item.variant === 'danger'
                              ? styles.riskCardLowStrike
                              : (isDesktop || isTablet)
                                ? styles.expiryCardDesktop
                                : styles.expiryCard,
                            (isDesktop || isTablet) && styles.productsAtRiskMiniCardDesktop,
                          ]}
                        >
                          <View
                            style={styles.productsAtRiskMiniCardHeader}
                          >
                            <Text
                              style={
                                item.variant === 'danger'
                                  ? styles.riskTitle
                                  : styles.productsAtRiskNeutralTitle
                              }
                              numberOfLines={2}
                            >
                              {item.title}
                            </Text>
                            <View
                              style={[
                                styles.cardIcon,
                                styles.productsAtRiskMiniCardIconPosition,
                                item.variant === 'danger' && styles.productsAtRiskIcon,
                              ]}
                            >
                              <Image
                                source={require('../../assets/icons/risk-icon.png')}
                                style={[
                                  styles.productsAtRiskIconImage,
                                  item.variant === 'neutral' &&
                                    styles.productsAtRiskIconImageNeutral,
                                ]}
                              />
                            </View>
                          </View>
                          {loadingSummary ? (
                            <ActivityIndicator size="small" color="#0891b2" />
                          ) : (
                            <Text
                              style={
                                item.variant === 'danger'
                                  ? styles.riskNumber
                                  : [
                                      styles.productsAtRiskNeutralNumber,
                                      item.key === 'barrier' &&
                                        styles.productsAtRiskNeutralNumberUnderlined,
                                    ]
                              }
                            >
                              {item.value}
                            </Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>

                  {isDesktop && (
                    <View style={styles.calendarContainer}>
                      <CalendarWidget
                        selectedWeekStart={selectedWeekStart}
                        onWeekChange={handleWeekChange}
                      />
                      {!isDefaultWeek && (
                        <TouchableOpacity
                          style={styles.clearFiltersButton}
                          onPress={handleClearFilters}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.clearFiltersButtonText}>Clear filters</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              </View>
            </View>
          ) : null}

          {/* Period Selector - Solo en mobile */}
          {!isDesktop && (
            <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
          )}

          <View style={styles.eventsContainer}>
            <Text style={styles.sectionTitle}>Events · {getWeekRangeString()}</Text>
            {filteredEvents.length > 0 ? (
              (() => {
                // En mobile: 1 por fila, en tablet/desktop: 2 por fila
                const itemsPerRow = width < 768 ? 1 : 2;
                const eventGroups = [];
                for (let i = 0; i < filteredEvents.length; i += itemsPerRow) {
                  eventGroups.push(filteredEvents.slice(i, i + itemsPerRow));
                }
                return eventGroups.map((group, groupIndex) => (
                  <View key={`group-${groupIndex}`} style={styles.eventsRow}>
                    {group.map((event, index) => (
                      <View key={`${event.productId}-${groupIndex}-${index}`} style={styles.eventItemWrapper}>
                        <EventItem
                          isin={event.isin}
                          productName={event.productName}
                          eventDescription={event.eventDescription}
                          eventDate={event.eventDate}
                          amount={event.amount}
                          notional={event.notional ?? 0}
                          couponPercent={event.couponPercent ?? 0}
                          firstCouponAmountPct={event.firstCouponAmountPct ?? 0}
                          currency={event.currency}
                          formatAmount={formatAmount}
                          formatDate={formatDate}
                          onPress={() => onProductPress?.(event.isin)}
                        />
                      </View>
                    ))}
                    {/* Si hay un solo evento en la última fila en tablet/desktop, agregar un espacio vacío */}
                    {group.length === 1 && itemsPerRow === 2 && <View style={styles.eventItemWrapper} />}
                  </View>
                ));
              })()
            ) : (
              <View style={styles.noEventsContainer}>
                <Text style={styles.noEventsText}>No events for this week</Text>
              </View>
            )}
          </View>
          </ScrollView>
          {loadingEvents && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#0891b2" />
              <Text style={styles.loadingText}>Loading events...</Text>
            </View>
          )}
        </View>
      </SafeAreaView>
    </ScreenTransition>
  );
};
